import { motion } from "motion/react";
import { useLocation, useOutletContext } from "react-router-dom";
import { useState, useEffect } from "react";
import { Globe, Music, Activity } from "lucide-react";
import { AnimatedDivider } from "../components/AnimatedDivider";
import { Helmet } from "react-helmet-async";

function WhiteBar() {
  return (
    <motion.div 
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.5, ease: [0.19, 1, 0.22, 1] }}
      className="h-[3px] w-full bg-white my-16 origin-left" 
    />
  );
}

export function Services() {
  const location = useLocation();
  const { hasLoaded, handleNavClick, isScrolledPop } = useOutletContext<{ hasLoaded: boolean, handleNavClick: (e: React.MouseEvent<HTMLAnchorElement>, href: string) => void, isScrolledPop?: boolean }>();
  const [isDocking, setIsDocking] = useState(() => Boolean(hasLoaded && location.state?.fromNav));

  useEffect(() => {
    if (isDocking) {
      const timer = setTimeout(() => setIsDocking(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isDocking]);

  return (
    <>
      <Helmet>
        <title>Services • Atharva Dhekne | tacitonic</title>
        <meta name="description" content="Precision stereo mastering, immersive mastering and mixing (Dolby Atmos, Sony Reality360, IAMF Eclipsa Audio), spectral audio restoration, production, and music technology consulting." />
        <meta name="robots" content="index, follow, max-image-preview:none" />
        <meta name="googlebot" content="index, follow, max-image-preview:none" />
        <link rel="canonical" href="https://tacitonic.com/services" />
        <meta property="og:title" content="Services • Atharva Dhekne | tacitonic" />
        <meta property="og:description" content="Precision stereo mastering, immersive mastering and mixing (Dolby Atmos, Sony Reality360, IAMF Eclipsa Audio), spectral audio restoration, production, and music technology consulting." />
        <meta property="og:url" content="https://tacitonic.com/services" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Audio Mastering & Immersive Audio Engineering",
            "provider": {
              "@id": "https://tacitonic.com/#person"
            },
            "areaServed": "Worldwide",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Audio Engineering & Consulting Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Stereo Mastering",
                    "description": "Precision stereo mastering for digital streaming on Spotify, Apple Music, Tidal, Amazon Music, Deezer, as well as physical media including CD, vinyl, and cassette."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Immersive Mastering",
                    "description": "Precision immersive mastering for Dolby Atmos, Sony Reality360, IAMF Eclipsa Audio, and other surround architectures."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Immersive Mixing",
                    "description": "Spatialized immersive mixing in Dolby Atmos, Sony Reality360, IAMF Eclipsa Audio, and other surround architectures."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Audio Restoration",
                    "description": "Surgical audio repair and restoration for archival, transfers from analog tape, cassette, and vinyl using advanced spectral editing."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Production",
                    "description": "Music production across a wide range of genres for album releases, theater, interactive audio, gaming, and other media."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Music Technology Consulting",
                    "description": "Strategic consulting on audio software development, hardware implementation, modern studio integration, and international market guidance."
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>
      <main className="pt-40 pb-32 px-6 max-w-7xl mx-auto min-h-screen">
      <div className="mb-16 pb-8 relative">
        <motion.h1 
          layoutId={isScrolledPop ? undefined : `page-title-Services`}
          initial={(!hasLoaded || isScrolledPop) ? { opacity: 0, scale: 0.95, y: 10 } : undefined}
          animate={{ opacity: 1, scale: 1, y: 0, color: "#FEFDF9" }}
          className="text-[31px] md:text-[52px] lg:text-[64px] font-bold tracking-tighter uppercase leading-none font-display w-fit origin-left text-white"
          transition={{ 
            duration: 1.2,
            ease: [0.16, 1, 0.3, 1],
            layout: { duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }
          }}
        >
          Services
        </motion.h1>
        {!isDocking && (
          <div className="absolute bottom-0 left-0 w-full">
            <AnimatedDivider className="h-[3px] bg-white" />
          </div>
        )}
      </div>

      <div className={`transition-opacity duration-700 delay-300 ${isDocking ? 'opacity-0' : 'opacity-100'}`}>
        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
        <h2 className="sr-only">Core Pillars</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-24">
          <div className="flex flex-col border-2 border-white/20 hover:border-white transition-all duration-300 p-4 md:p-8 group hover-target overflow-hidden">
            <div className="flex justify-between items-start mb-8 border-b-[3px] border-white pb-4 gap-4">
              <h3 className="text-[20px] md:text-[25px] font-bold uppercase tracking-tight font-display pr-8 flex-1 min-w-0">International Standards</h3>
              <Globe className="opacity-40 group-hover:opacity-100 transition-opacity shrink-0 mt-1" size={32} />
            </div>
            <p className="text-base md:text-lg opacity-60 leading-relaxed font-responsive-bold">
              Adhering to the highest industry standards and deliverable specifications for all global major labels.
            </p>
          </div>
          <div className="flex flex-col border-2 border-white/20 hover:border-white transition-all duration-300 p-4 md:p-8 group hover-target overflow-hidden">
            <div className="flex justify-between items-start mb-8 border-b-[3px] border-white pb-4 gap-4">
              <h3 className="text-[20px] md:text-[25px] font-bold uppercase tracking-tight font-display pr-8 flex-1 min-w-0">Cultural Nuance</h3>
              <Music className="opacity-40 group-hover:opacity-100 transition-opacity shrink-0 mt-1" size={32} />
            </div>
            <p className="text-base md:text-lg opacity-60 leading-relaxed font-responsive-bold">
              Strategic partnerships and deep contextual insight of highly diverse global music landscapes, seamlessly transitioning between genres.
            </p>
          </div>
          <div className="flex flex-col border-2 border-white/20 hover:border-white transition-all duration-300 p-4 md:p-8 group hover-target overflow-hidden">
            <div className="flex justify-between items-start mb-8 border-b-[3px] border-white pb-4 gap-4">
              <h3 className="text-[20px] md:text-[25px] font-bold uppercase tracking-tight font-display pr-8 flex-1 min-w-0">Technical Expertise</h3>
              <Activity className="opacity-40 group-hover:opacity-100 transition-opacity shrink-0 mt-1" size={32} />
            </div>
            <p className="text-base md:text-lg opacity-60 leading-relaxed font-responsive-bold">
              Leveraging industry-leading apparatus and critical listening to deliver the benchmark of audio mastering.
            </p>
          </div>
        </div>

        <div className="space-y-16 mb-24">
          <div className="relative pb-8 mb-8">
            <h2 className="text-[31px] md:text-[52px] font-bold uppercase tracking-tight font-display">Specialization</h2>
            <div className="absolute bottom-0 left-0 w-full">
              <AnimatedDivider className="h-[3px] bg-white" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="space-y-4">
              <h3 className="text-[25px] font-bold uppercase tracking-tight font-display">Stereo Mastering</h3>
              <p className="text-xl opacity-80 leading-relaxed">
                Precision stereo mastering for digital streaming on Spotify, Apple Music, Tidal, Amazon Music, Deezer, as well as physical media including CD, vinyl, and cassette. Uncompromised translation across the entire spectrum of playback systems.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-[25px] font-bold uppercase tracking-tight font-display">Immersive Mastering</h3>
              <p className="text-xl opacity-80 leading-relaxed">
                Precision immersive mastering for Dolby Atmos, Sony Reality360, IAMF Eclipsa Audio, and other surround architectures.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-[25px] font-bold uppercase tracking-tight font-display">Immersive Mixing</h3>
              <p className="text-xl opacity-80 leading-relaxed">
                Spatialized immersive mixing in Dolby Atmos, Sony Reality360, IAMF Eclipsa Audio, and other surround architectures.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-[25px] font-bold uppercase tracking-tight font-display">Restoration</h3>
              <p className="text-xl opacity-80 leading-relaxed">
                Surgical audio repair and restoration for archival, transfers from analog tape, cassette, and vinyl. Complete audio restoration using advanced spectral editing to reclaim obscured fidelity.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-[25px] font-bold uppercase tracking-tight font-display">Production</h3>
              <p className="text-xl opacity-80 leading-relaxed">
                Music production across a wide range of genres for album releases, theater, interactive audio, gaming, and other media.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-[25px] font-bold uppercase tracking-tight font-display">Music Technology Consulting</h3>
              <p className="text-xl opacity-80 leading-relaxed">
                Strategic consulting on audio software development, hardware implementation, and modern studio integration. International market guidance for audio and music technology products and services.
              </p>
            </div>
          </div>
        </div>

        {/* Formats Section */}
        <div className="space-y-12 mb-24">
          <div className="relative pb-8 mb-8">
            <h2 className="text-[25px] md:text-[40px] font-bold uppercase tracking-tighter font-display">Formats</h2>
            <div className="absolute bottom-0 left-0 w-full">
              <AnimatedDivider className="h-[3px] bg-white" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap md:gap-4">
            {["Lossless Digital", "Compact Disc (CD)", "Vinyl", "Analog Tape", "Cassette", "Apple Digital Masters", "Dolby Atmos", "Sony 360 Reality Audio", "Eclipsa Audio"].map(format => (
              <div key={format} className="border-2 border-white/20 pl-5 pr-4 py-3 md:px-6 md:py-3 font-mono text-[10px] md:text-base uppercase tracking-widest hover:bg-white hover:text-black transition-colors cursor-none text-center flex items-center justify-center font-bold">
                {format}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-8 md:gap-12 pt-4" data-nosnippet>
            <img
              src="https://media.tacitonic.com/media/Apple-Lossless-logo.svg"
              alt="Apple Lossless"
              width={150}
              height={90}
              loading="lazy"
              decoding="async"
              className="h-8 md:h-11 w-auto object-contain brightness-0 invert opacity-90 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://media.tacitonic.com/media/Compact-Disc-logo.svg"
              alt="Compact Disc Digital Audio"
              width={437}
              height={268}
              loading="lazy"
              decoding="async"
              className="h-7 md:h-10 w-auto object-contain brightness-0 invert opacity-90 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://media.tacitonic.com/media/LP-logo.svg#svgView(viewBox(0,0,230,230))"
              alt="LP Vinyl"
              width={230}
              height={230}
              loading="lazy"
              decoding="async"
              className="h-8 md:h-11 w-auto object-contain brightness-0 invert opacity-90 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://media.tacitonic.com/media/Compact-Cassette-logo.svg"
              alt="Compact Cassette"
              width={1452}
              height={444}
              loading="lazy"
              decoding="async"
              className="h-7 md:h-10 w-auto object-contain brightness-0 invert opacity-90 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://media.tacitonic.com/cdn-cgi/image/width=213,quality=80,format=webp/media/Apple-Digital-Masters-logo.webp"
              srcSet="https://media.tacitonic.com/cdn-cgi/image/width=213,quality=80,format=webp/media/Apple-Digital-Masters-logo.webp 1x, https://media.tacitonic.com/cdn-cgi/image/width=426,quality=80,format=webp/media/Apple-Digital-Masters-logo.webp 2x"
              alt="Apple Digital Masters"
              width={213}
              height={64}
              loading="lazy"
              decoding="async"
              className="h-12 md:h-16 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://media.tacitonic.com/cdn-cgi/image/width=130,quality=80,format=webp/media/Dolby-Atmos-Vertical-logo.webp"
              srcSet="https://media.tacitonic.com/cdn-cgi/image/width=130,quality=80,format=webp/media/Dolby-Atmos-Vertical-logo.webp 1x, https://media.tacitonic.com/cdn-cgi/image/width=260,quality=80,format=webp/media/Dolby-Atmos-Vertical-logo.webp 2x"
              alt="Dolby Atmos"
              width={130}
              height={48}
              loading="lazy"
              decoding="async"
              className="h-9 md:h-12 w-auto object-contain brightness-0 invert opacity-90 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://media.tacitonic.com/cdn-cgi/image/width=142,quality=80,format=webp/media/360RA-logo.webp"
              srcSet="https://media.tacitonic.com/cdn-cgi/image/width=142,quality=80,format=webp/media/360RA-logo.webp 1x, https://media.tacitonic.com/cdn-cgi/image/width=284,quality=80,format=webp/media/360RA-logo.webp 2x"
              alt="Sony 360 Reality Audio"
              width={142}
              height={80}
              loading="lazy"
              decoding="async"
              className="h-14 md:h-20 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://media.tacitonic.com/cdn-cgi/image/width=204,quality=80,format=webp/media/Eclipsa-Audio-logo.webp"
              srcSet="https://media.tacitonic.com/cdn-cgi/image/width=204,quality=80,format=webp/media/Eclipsa-Audio-logo.webp 1x, https://media.tacitonic.com/cdn-cgi/image/width=408,quality=80,format=webp/media/Eclipsa-Audio-logo.webp 2x"
              alt="Eclipsa Audio"
              width={204}
              height={40}
              loading="lazy"
              decoding="async"
              className="h-7 md:h-10 w-auto object-contain invert opacity-90 hover:opacity-100 transition-opacity"
            />
          </div>
        </div>
      </motion.div>
      </div>
    </main>
    </>
  );
}
